/**
 * `autopilot` namespace — English. Declared against the French source type, so a
 * missing or extra key fails the build.
 */

import type { AutopilotKeys } from './fr';

const autopilotEn: AutopilotKeys = {
  nav: {
    label: 'Autopilot',
  },
  page: {
    title: 'Autopilot from a URL',
    subtitle:
      'Paste your SaaS URL: Karexia understands the product, builds the strategy, prepares content, communities and measurement — and you approve every external action.',
    newProduct: 'New product',
    loading: 'Loading the autopilot…',
    loadError: 'Could not load the autopilot.',
    retry: 'Retry',
    switcherLabel: 'Product',
    noRuns: 'No product on autopilot yet.',
  },
  usage: {
    label: 'Products on autopilot: {{used}} / {{limit}}',
    unlimited: 'Products on autopilot: {{used}} (unlimited)',
    unknown: 'Products on autopilot: {{used}}',
    atLimit: 'You have reached the number of products in your plan.',
    upgrade: 'Upgrade your plan',
  },
  wizard: {
    steps: {
      url: '1 · Your URL',
      brief: '2 · The brief',
      channels: '3 · Channels and autonomy',
      run: '4 · Run',
      result: '5 · Result',
    },
    url: {
      label: 'Your SaaS or app address',
      placeholder: 'https://your-product.com',
      attest: 'I confirm this site / listing is mine and I allow Karexia to read at most 5 of its pages (robots.txt honoured).',
      submit: 'Analyse my product',
      analysing: 'Reading your pages…',
      invalid: 'Enter a valid address.',
      attestRequired: 'First confirm the site is yours.',
      needsText: 'Your site cannot be read automatically. Paste the text of your home and pricing pages.',
      pasteLabel: 'Your page text',
      pasteSubmit: 'Analyse this text',
      error: 'Analysis failed: {{error}}',
      briefLimit: 'This is a new product and your plan’s product briefs are all used. Upgrade to add another product, or delete a brief you no longer need.',
    },
    brief: {
      title: 'Here is what we understood',
      name: 'Product',
      headline: 'Headline (verbatim)',
      offer: 'Offer',
      audience: 'Audience',
      pricing: 'Pricing',
      stage: 'Stage',
      pages: '{{count}} page(s) read',
      edit: 'Edit the brief',
      looksRight: 'Looks right, continue',
      back: 'Back',
      empty: '—',
      reused: 'Existing brief v{{version}} reused (no credits)',
      reread: 'Re-read my site',
    },
    channels: {
      title: 'Channels you can act on now',
      hint: 'Ranked for your product. Untick the ones you do not want.',
      optionalTitle: 'Optional channels you can activate',
      optionalHint: 'Nothing is mandatory: the autopilot works with whatever is active. Activate one to add it.',
      activate: 'Activate',
      none: 'No channel selected.',
      autonomy: 'Autonomy',
      autonomyDraft: 'I approve the plan before content (recommended)',
      autonomySuggest: 'Prepare everything at once — I approve each draft afterwards',
      autonomyNote: 'Either way, nothing is published or sent without your approval.',
      language: 'Content language',
      estimateTitle: 'Credit estimate',
      estimateTotal: 'Estimated total: {{total}} credits',
      estimateAvailable: 'Available balance: {{available}} credits',
      estimateUnlimited: 'Unlimited credits',
      estimateInsufficient: 'Balance too low: the autopilot will pause and resume after a top-up.',
      estimateStep: '{{step}}: {{credits}}',
      start: 'Start the autopilot',
      starting: 'Starting…',
      back: 'Back',
    },
    run: {
      title: 'The autopilot is working',
      progress: 'Step {{current}} of {{total}}',
      stop: 'Pause the display',
    },
  },
  languages: {
    en: 'English',
    fr: 'French',
    ar: 'Arabic',
  },
  channels: {
    communities: 'Communities (HN, Bluesky, Dev.to)',
    blog_seo: 'Blog and SEO',
    launch_directories: 'Launch directories',
    waitlist_landing: 'Waitlist',
    email_nurture: 'Welcome e-mails',
    outbound_email: 'Outbound e-mail',
    referral: 'Referrals',
    app_store_aso: 'App Store listing (ASO)',
    linkedin: 'LinkedIn',
    x: 'X',
    bluesky: 'Bluesky',
    threads: 'Threads',
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    reddit: 'Reddit',
    other: 'Other sources',
    direct: 'Direct',
    unattributed: 'Unattributed',
  },
  reasons: {
    pre_launch: 'pre-launch',
    b2b_audience: 'B2B audience',
    consumer_audience: 'consumer audience',
    developer_audience: 'technical audience',
    high_price: 'higher price point',
    mobile_app: 'mobile app',
    has_users: 'already has users',
    strategy_mix: 'favoured by the strategy',
    works_without_accounts: 'works without connected accounts',
    already_launched: 'already launched',
  },
  requires: {
    'social:linkedin': 'Connect LinkedIn',
    'social:twitter': 'Connect X',
    'social:bluesky': 'Connect Bluesky',
    'social:threads': 'Connect Threads',
    'social:instagram': 'Connect Instagram',
    'social:facebook': 'Connect Facebook',
    'social:tiktok': 'Connect TikTok',
    reddit_api: 'Reddit API access',
    stripe: 'Connect Stripe',
    gsc: 'Connect Search Console',
    github: 'Connect GitHub',
    sdk: 'Install Karexia tracking',
    verified_domain: 'Verify your domain',
    generic: 'Activate',
  },
  stepNames: {
    understand: 'Understand the product',
    strategy: 'Strategy and 30-day plan',
    measurement: 'Measurement',
    launch_assets: 'Launch assets',
    content: 'Content engine',
    community: 'Community acquisition',
    outbound: 'Outbound',
    growth_loops: 'Growth loops',
    daily_ops: 'Daily operation',
  },
  stepHints: {
    understand: 'Brief from your pages (≤ 5 pages, robots.txt honoured) and product type.',
    strategy: 'ICP, positioning, ranked channels and a 30-day plan.',
    measurement: 'Tracking key, snippet to install, visitors → signups → paid funnel, UTM conventions.',
    launch_assets: 'Site audit (verified domain), launch kit, waitlist, build in public.',
    content: 'Two weeks of posts and articles in the approval queue.',
    community: 'Conversation Finder keywords; replies to copy, never posted for you.',
    outbound: 'ICP prospect criteria and a draft sequence (manual send, consent required).',
    growth_loops: 'Referral program, welcome e-mail and in-app checklist as drafts.',
    daily_ops: 'Your daily moves now come from this plan.',
  },
  status: {
    pending: 'Pending',
    running: 'Running',
    needs_approval: 'To approve',
    needs_credits: 'Credits needed',
    done: 'Done',
    skipped_optional: 'Optional',
    skipped_with_reason: 'Skipped',
    blocked_external: 'Access missing',
    failed: 'Failed',
    cancelled: 'Cancelled',
  },
  stepReasons: {
    needs_text: 'Paste your page text to continue.',
    plan_review: 'Review the plan, then approve it.',
    plan_approved: 'Plan approved.',
    insufficient_credits: 'Not enough credits for this step.',
    skipped_by_user: 'Skipped by you.',
    channel_not_selected: 'Channel not selected.',
    no_content_channel: 'No content channel selected.',
    not_in_plan: 'Not included in your plan.',
    service_unavailable: 'Service temporarily unavailable.',
    analytics_unavailable: 'Measurement temporarily unavailable.',
    services_unavailable: 'Services temporarily unavailable.',
    no_requested_channel_active: 'None of the requested channels is active.',
    no_draft_generated: 'No draft generated.',
    outbound_unavailable: 'Outbound temporarily unavailable.',
    no_brief: 'No brief.',
    brief_unavailable: 'The brief could not be read.',
    error: 'Unexpected error.',
    guardrail: 'Action blocked by the guardrails.',
    plan_limit_reached: 'Plan limit reached — upgrade to add more.',
    brief_not_found: 'This product brief no longer exists.',
  },
  planLimit: {
    skipped: 'Skipped (plan limit reached): {{items}}.',
    upgrade: 'Upgrade your plan',
    items: {
      launch_kit: 'launch kit',
      waitlist: 'waitlist page',
      ingest_key: 'tracking key',
      funnel: 'funnel',
      catch_product: 'Conversation Finder product',
      keywords_limit: 'Conversation Finder keywords',
      program: 'referral program',
      sequence: 'e-mail sequence',
      lifecycle: 'welcome e-mail',
      checklist: 'onboarding checklist',
      voice: 'brand voice',
    },
  },
  cockpit: {
    title: 'Autopilot cockpit',
    timeline: 'Steps',
    approve: 'To approve',
    funnel: 'Funnel by channel',
    moves: 'Next daily moves',
    connect: 'Add more channels',
    connectHint: 'Optional: each connection adds data or a channel.',
    snippetTitle: 'Tracking snippet to install',
    snippetHint: 'Paste it in the <head> of {{site}} to measure visitors.',
    copy: 'Copy',
    copied: 'Copied ✓',
    plan: '30-day plan',
    planItems: '{{count}} planned action(s)',
    positioning: 'Positioning',
    selectedChannels: 'Active channels',
    approvePlan: 'Approve the plan',
    skip: 'Skip',
    resume: 'Resume',
    continue: 'Continue',
    cancel: 'Stop the autopilot',
    cancelConfirm: 'Stop the autopilot for this product? Undecided drafts will be deleted.',
    cancelled: 'Autopilot stopped.',
    topUp: 'Top up my credits',
    creditsPaused: 'Paused: {{needed}} credits needed, {{available}} available.',
    open: 'Open',
    outputsLink: 'View',
    pasteText: 'Paste your page text',
    pasteSubmit: 'Continue with this text',
    allDone: 'Everything is ready. Approve the drafts below; nothing goes out without you.',
    noPending: 'Nothing to approve right now.',
    queueLink: 'Open the approval queue',
    deckLink: 'Open the swipe deck',
    audit: 'Decision history',
    auditEmpty: 'No decision yet.',
    todayLink: 'Open Today',
    noMoves: 'Moves appear after the "Daily operation" step.',
    actionError: 'Action failed: {{error}}',
  },
  inbox: {
    approve: 'Approve',
    reject: 'Reject',
    approved: 'Approved',
    rejected: 'Rejected',
    plannedFor: 'Planned for {{date}}',
    open: 'Open',
    noPublish: 'Approving publishes nothing: the draft joins the approval / scheduling queue.',
    kinds: {
      post: 'Post',
      article: 'Article',
      email_sequence: 'E-mail sequence',
      prospect_criteria: 'Prospect criteria',
      referral_program: 'Referral program',
      lifecycle_email: 'Welcome e-mail',
      checklist: 'In-app checklist',
      waitlist: 'Waitlist page',
      launch_kit: 'Launch kit',
    },
  },
  audit: {
    start: 'Started',
    attest: 'Ownership confirmed',
    approve: 'Approved',
    reject: 'Rejected',
    skip: 'Step skipped',
    resume: 'Resumed',
    cancel: 'Stopped',
  },
  funnel: {
    channel: 'Channel',
    visitors: 'Visitors',
    signups: 'Signups',
    paid: 'Paying',
    mrr: 'MRR',
    searchClicks: 'Search clicks',
    period: 'Last {{days}} days',
    total: 'Total',
    sources: {
      sdk: 'Site tracking',
      stripe: 'Stripe',
      gsc: 'Search Console',
    },
    sourceStatus: {
      connected: 'connected',
      not_connected: 'not connected',
      unavailable: 'unavailable',
      not_in_plan: 'not in plan',
    },
    connectHint: 'Connect it to see these numbers.',
    loading: 'Loading the funnel…',
    error: 'Funnel unavailable right now.',
  },
  outputs: {
    brief: 'Brief v{{version}}',
    kindWeb: 'Web SaaS',
    kindMobile: 'Mobile app',
    keywords: '{{count}} keyword(s) added',
    drafts: '{{count}} draft(s)',
    sequenceDraft: 'Draft sequence',
    manualSend: 'Manual send, after consent',
  },
  today: {
    title: 'Autopilot',
    body: 'Paste your SaaS URL: Karexia prepares everything up to your first customers.',
    cta: 'Open the autopilot',
  },
};

export default autopilotEn;
